import { useMemo } from 'react'

import useUser from '@/pages/projectList/hooks/useUser'
import { canCreateProject } from '@/pages/projectList/utils'
import { isAdminUser } from '@/utils/user'
import { Permission, ProductType, toPermission } from '@infraloop/services'
import { useSearchParams } from 'react-router'

export interface DashboardPermissions {
  /** Create / read / update / delete a project (and its inspection areas). */
  project: Permission
  /** True when the viewer is an admin — they may browse any dashboard but not mutate it. */
  isReadOnly: boolean
  /** True when an admin is browsing another user's dashboard (`?user_id=`), not their own. */
  isViewingOtherUser: boolean
  /** True when the Created projects tab should be shown. */
  hasBaseAccess: boolean
}

/**
 * Dashboard permissions for the signed-in user. Mutations require a paying subscription
 * (the Hatsuly or Markly product); admins are read-only — they can open any user's
 * dashboard from the user list to look, but never mutate it.
 */
const usePermissions = (): DashboardPermissions => {
  const { user } = useUser()
  const [searchParams] = useSearchParams()

  const isReadOnly = isAdminUser(user)
  const canModify = canCreateProject(user)
  // Only admins may view another user's dashboard; `useProjects` reads `user_id` under the
  // same guard, so the two stay in step on which dashboard is being shown.
  const isViewingOtherUser = isReadOnly && Boolean(searchParams.get('user_id'))
  // The base subscription gates only whether the Created tab is shown, so while an admin
  // browses another user's dashboard it follows the viewed user (who owns the projects the
  // admin came to see) rather than the admin's own products. It stays off on the admin's own
  // dashboard, where the tab would be an empty control they can't act on.
  const hasBaseAccess =
    isViewingOtherUser || user?.owned_products.includes(ProductType.Base) || user?.is_beta_user || false

  return useMemo(
    () => ({
      project: toPermission(canModify, { readOnly: isReadOnly }),
      isReadOnly,
      isViewingOtherUser,
      hasBaseAccess,
    }),
    [canModify, isReadOnly, isViewingOtherUser, hasBaseAccess],
  )
}

export default usePermissions
